/**
 * Module-level pure helpers for Product Costing, Fractional BOM Norms & Catalog Pricing (Rule 11 safe).
 * Never nest these inside React hooks or closures to avoid Vite minification/tree-shaking issues.
 */

export function toNumericValue(raw: unknown): number {
  if (raw == null || raw === "") return 0;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : 0;
  if (typeof raw === "object" && typeof (raw as any).toNumber === "function") {
    const n = (raw as any).toNumber();
    return Number.isFinite(n) ? n : 0;
  }
  const str = String(raw).trim().replace(/,/g, ".");
  const parsed = Number(str);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatBomNorm(val: unknown, maxDecimals = 4): string {
  const num = toNumericValue(val);
  if (!Number.isFinite(num) || num === 0) return "0";
  return new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(num);
}

export function parseMarginPercent(raw: unknown, fallback = 0): number {
  if (raw == null || raw === "") return fallback;
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw >= 0 ? raw : fallback;
  }
  const cleaned = String(raw)
    .replace(/%/g, "")
    .replace(/,/g, ".")
    .trim();
  if (!cleaned) return fallback;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export function computeCatalogPriceFromCost(
  unitCost: number,
  marginPercent = 0,
): number {
  const safeCost = toNumericValue(unitCost);
  if (safeCost <= 0) return 0;
  const safeMargin = Math.max(0, toNumericValue(marginPercent));
  return Math.round(safeCost * (1 + safeMargin / 100));
}

export function resolveProductCatalogPricing(orig: any): {
  catalogPrice: number;
  costPrice: number;
  profitMargin: number;
} {
  if (!orig || typeof orig !== "object") {
    return { catalogPrice: 0, costPrice: 0, profitMargin: 0 };
  }

  const explicitPrice =
    toNumericValue(orig.price) ||
    toNumericValue(orig.priceVND) ||
    toNumericValue(orig.unitPrice);

  const costPrice =
    toNumericValue(orig.costPrice) || toNumericValue(orig.standardCost);

  const profitMargin = parseMarginPercent(
    orig.profitMargin ?? orig.productType?.profitMargin,
    0,
  );

  // Giá bán chỉ lấy đơn giá niêm yết kinh doanh đã nhập. Sản phẩm chưa có giá → 0 để kinh doanh tự nhập,
  // không tự suy ra từ giá vốn × % lãi (tính giá còn đang phát triển, chưa chính xác).
  return {
    catalogPrice: explicitPrice > 0 ? Math.round(explicitPrice) : 0,
    costPrice: Math.round(costPrice),
    profitMargin,
  };
}
